/**
 * This file provides type-safe style interpolation for common themed elements.
 */
import { SourceType, ThemeName, ThemeProps, WithThemeName } from "./types";

export const DARK_CODE_BG = "rgb(39, 44, 53)";
export const DARK_SECONDARY_BG = "rgb(53, 59, 71)";
export const BLACK = "rgb(0, 0, 0)";
export const WHITE = "rgb(255, 255, 255)";

/**
 * Allows specification of the type of media to target in a media query.
 *
 * @remarks
 * This should be the first argument to `styledMedia` if used:
 * ```ts
 * styledMedia(MediaType.ONLY_SCREEN, BreakpointDown.S)`
 *  display: none;
 * `;
 * ```
 */
export enum MediaType {
  ALL = "all",
  PRINT = "print",
  ONLY_PRINT = "only print",
  NOT_PRINT = "not print",
  SCREEN = "screen",
  ONLY_SCREEN = "only screen",
  NOT_SCREEN = "not screen",
  SPEECH = "speech",
  ONLY_SPEECH = "only speech",
  NOT_SPEECH = "not speech",
}

/**
 * @deprecated. use MaterialBreakpointDown for breakpoints that match material specs
 * Responsive breakpoints for viewport smaller than width (based on Bootstrap + Jupyter).
 */
export enum BreakpointDown {
  // Where Jupyters notebook cell responsive breakpoint is.
  /** iPhone, Samsung Galaxy */
  XXXXS = "(max-width: 375px)",
  /** Pixel */
  XXXS = "(max-width: 480px)",
  XXS = "(max-width: 540px)",
  XS = "(max-width: 575px)",
  S = "(max-width: 671px)",
  M = "(max-width: 840px)",
  L = "(max-width: 991px)",
  XL = "(max-width: 1199px)",
  // KM aliases
  PHONE = "(max-width: 480px)",
  TABLET = "(max-width: 840px)",
  DESKTOP = "(max-width: 1199px)",
}

/**
 * @deprecated. use MaterialBreakpointUp for breakpoints that match material specs
 * Responsive breakpoints for viewport greater than width (based on Bootstrap + Jupyter).
 */
export enum BreakpointUp {
  // Where Jupyters notebook cell responsive breakpoint is.
  XXXXS = "(min-width: 376px)",
  XXXS = "(min-width: 481px)",
  XXS = "(min-width: 541px)",
  XS = "(min-width: 576px)",
  S = "(min-width: 672px)",
  M = "(min-width: 841px)",
  L = "(min-width: 992px)",
  XL = "(min-width: 1200px)",
  // KM aliases
  PHONE = "(min-width: 481px)",
  TABLET = "(min-width: 841px)",
  DESKTOP = "(min-width: 1200px)",
}

/**
 * These are the actual material breapoints
 * https://carbon.googleplex.com/kaggle/pages/layout-breakpoints/principles
 */
export enum MaterialBreakpointDown {
  XS1 = "(max-width: 359px)",
  XS2 = "(max-width: 399px)",
  XS3 = "(max-width: 479px)",
  SM1 = "(max-width: 599px)",
  SM2 = "(max-width: 719px)",
  SM3 = "(max-width: 839px)",
  MD1 = "(max-width: 959px)",
  MD2 = "(max-width: 1023px)",
  LG1 = "(max-width: 1279px)",
  LG2 = "(max-width: 1439px)",
  LG3 = "(max-width: 1599px)",
  XL1 = "(max-width: 1919px)",
  PHONE = XS3,
  TABLET = SM3,
  DESKTOP = LG1,
}

export enum MaterialBreakpointUp {
  XS1 = "(min-width: 360px)",
  XS2 = "(min-width: 400px)",
  XS3 = "(min-width: 480px)",
  SM1 = "(min-width: 600px)",
  SM2 = "(min-width: 720px)",
  SM3 = "(min-width: 840px)",
  MD1 = "(min-width: 960px)",
  MD2 = "(min-width: 1024px)",
  LG1 = "(min-width: 1280px)",
  LG2 = "(min-width: 1440px)",
  LG3 = "(min-width: 1600px)",
  XL1 = "(min-width: 1920px)",
  PHONE = XS3,
  TABLET = SM3,
  DESKTOP = LG1,
}

/** From Chelsea dark mode spec: https://gallery.googleplex.com/projects/MCHbtQVoQ2HCZVuSdlHNVFHB/files/MCEJu8Y2hyDScV7aW-meUk49Sy4yWXNJriI */
export const darkPalette = {
  accent: "#20beff",
  lightLines: "#6e789e",
  darkLines: "#4f5868",
  sideBars: "#353b47",
  windowBackground: "#262b35",
  cellBackground: "#161a20",
  text: {
    100: "#ffffff",
    70: "rgba(255,255,255,0.7)",
    50: "rgba(255,255,255,0.5)",
  },
  lineNumbers: "#526b77",
};

export const syntax = {
  dark: {
    green: "#B9DF83",
    white: "#D3E4E4",
    black: "#526B77",
    magenta: "#C18EE4",
    cyan: "#7FCAE9",
    red: "#F3516C",
    yellow: "#FFCB6A",
    orange: "#EE886A",
    blue: "#7FA7FA",
  },
};

/** Converts a style value pair to CSS format. If the value is null it strips the style. */
const interpolateStyle = (styleName: string, styleValue: string | null) =>
  styleName === null || styleValue === null
    ? ""
    : `${styleName}: ${styleValue}`;

/** Extracts the themes style from a map. */
const getThemeStyle = (styleMap: Record<ThemeName, string | null>) => (
  props: WithThemeName
) => styleMap[props.themeName];

/** Option to pass something direct or a function which returns it. */
type MaybeLazy<T, TProps> = T | ((props: TProps) => T);

/** Creates factories that switch styles based on theme name (light / dark ). */
export const themeNameStyle = (styleName: string) => <TProps>(
  valueMap: MaybeLazy<Record<ThemeName, string | null>, TProps & ThemeProps>
) => (props: TProps & ThemeProps, omitName?: boolean) => {
  const mapResult = typeof valueMap === "function" ? valueMap(props) : valueMap;
  const styleValue = getThemeStyle(mapResult)(props.theme.session);
  if (omitName === true) {
    return styleValue;
  }
  return interpolateStyle(styleName, styleValue);
};

/** General function to interpolate background style by theme name. */
export const backgroundTheme = themeNameStyle("background");

/** Interpolates a code `background: <value>` prop into a styled-component, null values omit style for that theme. */
export const cellBackground = (lightColor: string | null = null) =>
  backgroundTheme({
    dark: darkPalette.cellBackground,
    light: lightColor,
  });

/** Interpolates a window `background: <value>` prop into a styled-component, null values omit style for that theme. */
export const windowBackground = (lightColor: string | null = null) =>
  backgroundTheme({
    dark: darkPalette.windowBackground,
    light: lightColor,
  });

/** Interpolates a sidebar `background: <value>` prop into a styled-component, null values omit style for that theme. */
export const sidebarBackground = () =>
  backgroundTheme({
    dark: darkPalette.sideBars,
    light: WHITE,
  });

/** Interpolates a menu `background: <value>` prop into a styled-component, null values omit style for that theme. */
export const menuBackground = () =>
  backgroundTheme({
    dark: darkPalette.darkLines,
    light: "#fff",
  });

/** Interpolates a menu button `background: <value>` prop into a styled-component, null values omit style for that theme. */
export const menuButtonBackground = () =>
  backgroundTheme({
    dark: darkPalette.darkLines,
    light: "#f0f0f0",
  });

/** Interpolates a hovered menu button `background: <value>` prop into a styled-component, null values omit style for that theme. */
export const menuButtonHoverBackground = () =>
  backgroundTheme({
    dark: darkPalette.sideBars,
    light: "#f0f0f0",
  });

/** Kaggle Material background color. */
export const materialBackground = () =>
  backgroundTheme({
    dark: "#3C4043", // GREY_800
    light: "#FFF", // KAGGLE_WHITE
  });

/** Kaggle Material background color for a hovered interactable element. */
export const materialHoverBackground = () =>
  backgroundTheme({
    dark: "#5F6368", // GREY_700
    light: "#F1F3F4", // GREY_100
  });

/** Kaggle Material background color for alternate elements. */
export const materialSecondaryBackground = () =>
  backgroundTheme({
    dark: "#202124", // GREY_900
    light: "#F8F9FA", // GREY_50
  });

type AccentType = "loading" | "caution" | "error" | "success";

export interface ButtonProps {
  disabled?: boolean;
}

/** Interpolates a brand button background color. Handles disabled property. */
export const brandButtonBackground = () =>
  backgroundTheme<ButtonProps>(props => ({
    light: props.disabled
      ? props.theme.material.color.brand.grey[800]
      : props.theme.material.color.brand.blue[800],
    dark: props.disabled
      ? props.theme.material.color.brand.grey[500]
      : props.theme.material.color.brand.blue[500],
  }));

/** Interpolates a brand button hover background color. Handles disabled property. */
export const brandButtonHoverBackground = () =>
  backgroundTheme<ButtonProps>(props => ({
    light: props.disabled
      ? props.theme.material.color.brand.grey[800]
      : props.theme.material.color.brand.blue[900],
    dark: props.disabled
      ? props.theme.material.color.brand.grey[500]
      : props.theme.material.color.brand.blue[600],
  }));

/** Interpolates a accent button background color. Handles disabled property. */
export const accentButtonBackground = (type: AccentType = "success") =>
  backgroundTheme<ButtonProps>(props => ({
    light: props.disabled
      ? props.theme.material.color.brand.grey[800]
      : props.theme.material.color.status[type][700],
    dark: props.disabled
      ? props.theme.material.color.brand.grey[500]
      : props.theme.material.color.status[type][500],
  }));

/** Interpolates a accent button hover background color. Handles disabled property. */
export const accentButtonHoverBackground = (type: AccentType = "success") =>
  backgroundTheme<ButtonProps>(props => ({
    light: props.disabled
      ? props.theme.material.color.brand.grey[800]
      : props.theme.material.color.status[type][900],
    dark: props.disabled
      ? props.theme.material.color.brand.grey[500]
      : props.theme.material.color.status[type][700],
  }));

/** Interpolates the terminal background color into a styled-component. */
export const terminalBackground = () =>
  backgroundTheme({
    dark: darkPalette.sideBars,
    light: darkPalette.sideBars,
  });

/** Interpolates the modal background color into a styled-component. */
export const modalBackground = () =>
  backgroundTheme({
    dark: darkPalette.sideBars,
    light: WHITE,
  });

/** Interpolates a material box shadow into a styled-component. */
export const shadow = () => `
  box-shadow: 0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12);
`;

type BorderStyleName =
  | "border"
  | "border-top"
  | "border-right"
  | "border-bottom"
  | "border-left";

const formatBorderStyle = (color: string) => `1px solid ${color}`;

/**
 * Interpolates a `border[-variant]: 1px solid <value>` prop into a styled-component.
 * Used in header and as a primary border style.
 */
export const primaryBorder = <TProps>(styleName: BorderStyleName = "border") =>
  themeNameStyle(styleName)((props: TProps & ThemeProps) => {
    return {
      light: formatBorderStyle(props.theme.colors.border),
      dark: formatBorderStyle(darkPalette.darkLines),
    };
  });

/**
 * Interpolates a `border[-variant]: 1px solid <value>` prop into a styled-component.
 * Used in sidebar and as a secondary border style.
 */
export const secondaryBorder = <TProps>(
  styleName: BorderStyleName = "border"
) =>
  themeNameStyle(styleName)((props: TProps & ThemeProps) => ({
    light: formatBorderStyle(props.theme.colors.border),
    dark: formatBorderStyle(darkPalette.lightLines),
  }));

/** Interpolates an brand button border color. Handles disabled property. */
export const brandButtonBorder = <TProps extends ButtonProps = ButtonProps>(
  styleName: BorderStyleName = "border"
) =>
  themeNameStyle(styleName)((props: TProps & ThemeProps) => ({
    light: formatBorderStyle(
      props.disabled
        ? props.theme.material.color.brand.grey[900]
        : props.theme.material.color.brand.blue[900]
    ),
    dark: formatBorderStyle(
      props.disabled
        ? props.theme.material.color.brand.grey[600]
        : props.theme.material.color.brand.blue[600]
    ),
  }));

/** Interpolates an accent button border color. Handles disabled property. */
export const accentButtonBorder = <TProps extends ButtonProps = ButtonProps>(
  styleName: BorderStyleName = "border",
  type: AccentType = "success"
) =>
  themeNameStyle(styleName)((props: TProps & ThemeProps) => ({
    light: formatBorderStyle(
      props.disabled
        ? props.theme.material.color.brand.grey[900]
        : props.theme.material.color.status[type][900]
    ),
    dark: formatBorderStyle(
      props.disabled
        ? props.theme.material.color.brand.grey[600]
        : props.theme.material.color.status[type][700]
    ),
  }));

/** Interpolates a `color: <value>` prop into a styled-component, null values omit style for that theme type. */
export const colorStyle = themeNameStyle("color");

export const getReadableColorValue = (
  themeName: ThemeName,
  level: 50 | 70 | 100 = 100
) => {
  const values = {
    light:
      level === 50
        ? "rgba(0,0,0,0.5)"
        : level === 70
        ? "rgba(0, 0, 0, 0.7)"
        : BLACK,
    dark: darkPalette.text[level],
  };
  return values[themeName];
};

/** Interpolates a readable color with varying degrees of darkness. 100 (default) is full black / white. */
export const readableColor = (level: 50 | 70 | 100 = 100) =>
  colorStyle({
    light:
      level === 50
        ? "rgba(0,0,0,0.5)"
        : level === 70
        ? "rgba(0, 0, 0, 0.7)"
        : BLACK,
    dark: darkPalette.text[level],
  });

/** Interpolates color style for button / theme. */
export const buttonColor = () =>
  colorStyle({
    light: "white",
    dark: darkPalette.cellBackground,
  });

/** Interpolates foreground color style for an input. */
export const inputColor = () =>
  colorStyle({
    light: BLACK,
    dark: darkPalette.text[100],
  });

/** Interpolates placeholder `color: <color>;` for an input. Does not control the placeholder tag. */
export const inputPlaceholderColor = () =>
  colorStyle({
    light: "rgba(0, 0, 0, 0.5)",
    dark: "rgba(255, 255, 255, 0.5)",
  });

/** Interpolates input background color. */
export const inputBackground = () =>
  backgroundTheme({
    dark: darkPalette.windowBackground,
    light: WHITE,
  });

/** An interpolation that returns a string. */
type Func<T, TResult> = (props: T) => TResult;

/** Represents any falsy type that should reduce to "" if returned from a styled tagged template literal interpolation. */
type Falsy = undefined | null | false;

/**
 * Represents an interpolation within a styled-component.
 *
 * @remarks
 * Below is an example of where the interpolation sits in a styled-component.
 * ```tsx
 * const Foo = styled`
 *   background: ${interpolation};
 * `;
 * ```
 *
 * The interpolation can be a value or a function that accepts a `(props: ThemeProps) => value`.
 *
 * The return value will be converted to a string and placed in the template. If its falsy it will be removed.
 * `;
 */
type Interpolation<T> = Falsy | number | string | Func<T, Falsy | string>;

/** Checks if a value is falsy, if it is it replaces with the passed string. */
const replaceFalsy = <T>(value: T | Falsy, replacement: string) =>
  value === undefined || value === null || value === false
    ? replacement
    : value;

/**
 * Creates a tagged template literal that will only be included if the condition is met.
 *
 * @remarks
 * The following example would add a `background: white;` style only if the theme is dark.
 * ```tsx
 * const Foo = styled`
 *   ${styledConditional(props => props.theme.name === "dark")`
 *     background: white;
 *   `}
 * `;
 * ```
 *
 * It is recommended to create smaller functions in this file to break down variations like the above.
 */
export const styledConditional = <T extends ThemeProps>(
  condition: Func<T, boolean>
) => {
  /** The tagged template literal. */
  const conditionalTemplateLiteral = (
    strings: TemplateStringsArray,
    ...interpolations: Interpolation<T>[]
  ) => {
    /** The styled interpolation that will be injected with the embedded literals props. */
    const styledInterpolation = (props: T) => {
      let style = "";
      // If its not of the correct source type, don't return the style.
      if (!condition(props)) {
        return style;
      }

      const interpolate = (interpolation: Interpolation<T>) => {
        // If the interpolation is a function, inject it with props.
        if (typeof interpolation === "function") {
          return interpolation(props);
        }
        // Its just a normal type, allow it to pass through.
        return interpolation;
      };

      // Iterate string templates and interpolate in styled theme props.
      strings.forEach((string, i) => {
        style += string + replaceFalsy(interpolate(interpolations[i]), "");
      });
      return style;
    };
    return styledInterpolation;
  };
  return conditionalTemplateLiteral;
};

/** Valid media query rules. */
type MediaRule =
  | MediaType
  | BreakpointUp
  | BreakpointDown
  | MaterialBreakpointDown
  | MaterialBreakpointUp;

/**
 * Creates a tagged template literal that will set styles for specific media queries.
 *
 * @remarks
 * This example would add the contained styles only if the page is below `M` breakpoint width.
 * ```tsx
 * const Foo = styled`
 *   ${styledMedia(BreakpointDown.M)`
 *     display: none;
 *   `}
 * `;
 * ```
 *
 * The following example would add the contained styles only if the page is above `XXS` and below `S` breakpoint widths.
 * ```tsx
 * const Foo = styled`
 *   ${styledMedia(BreakpointUp.XXS, BreakpointDown.S)`
 *     background: blue;
 *   `}
 * `;
 * ```
 *
 * `styledMedia` can accept any number of breakpoint parameters.
 */
export const styledMedia = <T extends ThemeProps>(...rules: MediaRule[]) => {
  /** The tagged template literal. */
  const mediaTemplateLiteral = (
    strings: TemplateStringsArray,
    ...interpolations: Interpolation<T>[]
  ) => {
    /** The styled interpolation that will be injected with the embedded literals props. */
    const styledInterpolation = (props: T) => {
      // Create a media tag with all passed rules joined (and).
      let style = `@media ${rules.join(" and ")} {\n`;

      const interpolate = (interpolation: Interpolation<T>) => {
        // If the interpolation is a function, inject it with props.
        if (typeof interpolation === "function") {
          return interpolation(props);
        }
        // Its just a normal type, allow it to pass through.
        return interpolation;
      };

      // Iterate string templates and interpolate in styled theme props.
      strings.forEach((string, i) => {
        style += string + replaceFalsy(interpolate(interpolations[i]), "");
      });
      // Close media tag.
      style += "\n}";
      return style;
    };
    return styledInterpolation;
  };
  return mediaTemplateLiteral;
};

/** Conditional tagged template literal that only applies style for the specified theme name. */
export const styledThemeName = (themeName: ThemeName) =>
  styledConditional(props => props.theme.name === themeName);

/** Conditional tagged template literal that only applies style for the specified source type. */
export const styledSourceType = (sourceType: SourceType) =>
  styledConditional(props =>
    props.theme.session ? props.theme.session.sourceType === sourceType : false
  );

/** Conditional requiring both a certain source type and theme name. */
export const styledTypeAndTheme = (
  sourceType: SourceType,
  themeName: ThemeName
) =>
  styledConditional(
    props =>
      props.theme.name === themeName &&
      (props.theme.session
        ? props.theme.session.sourceType === sourceType
        : false)
  );

/** Scrollbar overrides mixin. Used with injectGlobal in and out of frame. */
export const scrollbars: Record<ThemeName, string> = {
  light: `
&::-webkit-scrollbar {
  height: 6px;
  width: 6px;
  /* The scrollbar background color. */
  background: #ececed;
}
&::-webkit-scrollbar-thumb {
  /* The scrollbar foreground color. */
  background-color: #b5b6b8;
}
&::-webkit-scrollbar-thumb:hover {
  /* The scrollbar foreground color. */
  background-color: #959698;
}
&::-webkit-scrollbar-thumb:active {
  /* The scrollbar foreground color. */
  background-color: #757678;
}
`,
  dark: `
&::-webkit-scrollbar {
  height: 6px;
  width: 6px;
  /* The scrollbar background color. */
  background-color: ${darkPalette.windowBackground};
}
&::-webkit-scrollbar-thumb {
  /* The scrollbar foreground color. */
  background: ${darkPalette.darkLines};
}
&::-webkit-scrollbar-thumb:hover {
  /* The scrollbar foreground color. */
  background: #5f6876;
}
&::-webkit-scrollbar-thumb:active {
  /* The scrollbar foreground color. */
  background: #6f7484;
}
`,
};

/** Adds styles that make the current container show an ellipsis on overflow. */
export const ellipsis = (maxWidth: string) => `
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: ${maxWidth};
`;
